/**
 * Guardrails for the Mongo MCP connector: which collections Claude is even
 * allowed to query (allowlist, fail-closed — a collection with no row here
 * is not queryable), and which field names get stripped from the documents
 * it does see.
 *
 * builder-backend is the single authority for this config (Postgres-backed);
 * this panel writes to it directly. mcp-gateway enforces it by polling the
 * same read endpoint this panel reads from — there is no push, so a save
 * here takes effect at the gateway within its poll interval (~60s), not
 * instantly.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Select, Space, Spin, Tag, Transfer, Typography } from 'antd';
import type { TransferProps } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import {
  fetchMcpCollections,
  fetchMcpPolicy,
  fetchMcpSampleFields,
  saveMcpPolicy,
} from '../../services/api.service';
import type { McpCollectionPolicyInput } from '../../types/mcp';

const { Text, Paragraph } = Typography;

interface Props {
  serverId: string;
  database: string;
}

interface TransferRecord {
  key: string;
  title: string;
}

const McpGuardrailsPanel: React.FC<Props> = ({ serverId, database }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const [allCollections, setAllCollections] = useState<string[]>([]);
  const [allowedKeys, setAllowedKeys] = useState<string[]>([]);
  const [hiddenFieldsByCollection, setHiddenFieldsByCollection] = useState<Record<string, string[]>>({});
  const [fieldOptionsByCollection, setFieldOptionsByCollection] = useState<Record<string, string[]>>({});
  const [loadingFieldsFor, setLoadingFieldsFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [collections, policies] = await Promise.all([
        fetchMcpCollections(serverId),
        fetchMcpPolicy(serverId),
      ]);
      setAllCollections(collections);
      const allowed = policies.filter((p) => p.allowed);
      setAllowedKeys(allowed.map((p) => p.collection));
      setHiddenFieldsByCollection(
        Object.fromEntries(allowed.map((p) => [p.collection, p.hiddenFields]))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la política de Mongo');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => {
    void load();
  }, [load]);

  const dataSource: TransferRecord[] = useMemo(
    () => allCollections.map((name) => ({ key: name, title: name })),
    [allCollections]
  );

  const handleTransferChange: TransferProps<TransferRecord>['onChange'] = (nextTargetKeys) => {
    const next = nextTargetKeys.map(String);
    setAllowedKeys(next);
    // Drop hidden-field selections for collections that just got moved back
    // to "no permitida" — they're meaningless without an allowed collection.
    setHiddenFieldsByCollection((prev) => {
      const kept: Record<string, string[]> = {};
      for (const key of next) if (prev[key]) kept[key] = prev[key];
      return kept;
    });
  };

  async function loadFieldOptions(collection: string): Promise<void> {
    if (fieldOptionsByCollection[collection]) return;
    setLoadingFieldsFor(collection);
    try {
      const fields = await fetchMcpSampleFields(serverId, collection);
      setFieldOptionsByCollection((prev) => ({ ...prev, [collection]: fields }));
    } catch {
      // Sampling can fail (empty collection, transient gateway issue); the
      // admin can still type field names by hand in the tags-mode select.
      setFieldOptionsByCollection((prev) => ({ ...prev, [collection]: [] }));
    } finally {
      setLoadingFieldsFor(null);
    }
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const policies: McpCollectionPolicyInput[] = allowedKeys.map((collection) => ({
        collection,
        allowed: true,
        hiddenFields: hiddenFieldsByCollection[collection] ?? [],
      }));
      await saveMcpPolicy(serverId, database, policies);
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la política de Mongo');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card size="small" title="Guardrails: colecciones y campos">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  return (
    <Card size="small" title="Guardrails: colecciones y campos" style={{ marginBottom: 16 }}>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Solo las colecciones de la derecha son consultables por Claude — el resto
        se bloquea antes de llegar a Mongo. Para cada una, elige qué campos
        ocultar (se aplica a cualquier profundidad, p. ej. <Text code>contact.email</Text>
        {' '}se oculta con solo poner <Text code>email</Text>).
      </Paragraph>

      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}

      <Transfer<TransferRecord>
        dataSource={dataSource}
        titles={['No permitidas', 'Permitidas']}
        targetKeys={allowedKeys}
        onChange={handleTransferChange}
        showSearch
        filterOption={(input, item) => item.title.toLowerCase().includes(input.toLowerCase())}
        render={(item) => item.title}
        listStyle={{ width: 280, height: 320 }}
      />

      {allowedKeys.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <Text strong>Campos a ocultar por colección</Text>
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={12}>
            {[...allowedKeys].sort().map((collection) => (
              <div key={collection}>
                <Tag color="blue" style={{ marginBottom: 4 }}>{collection}</Tag>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="email, phoneNumber, ..."
                  value={hiddenFieldsByCollection[collection] ?? []}
                  options={(fieldOptionsByCollection[collection] ?? []).map((f) => ({ value: f, label: f }))}
                  loading={loadingFieldsFor === collection}
                  onFocus={() => void loadFieldOptions(collection)}
                  onChange={(values) =>
                    setHiddenFieldsByCollection((prev) => ({ ...prev, [collection]: values }))
                  }
                />
              </div>
            ))}
          </Space>
        </div>
      )}

      <Space style={{ marginTop: 20 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void handleSave()}>
          Guardar
        </Button>
        {savedAt && (
          <Text type="secondary">
            Guardado a las {savedAt.toLocaleTimeString('es-ES')} — el gateway lo recoge en su próximo sondeo (~60s)
          </Text>
        )}
      </Space>
    </Card>
  );
};

export default McpGuardrailsPanel;
